import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Clock3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getTask, getTaskSummary, listTaskLogs, listTasks, markTaskFailed, retryTask } from "../../api/tasks";
import { PaginationBar } from "../../components/pagination-bar";
import { Button } from "../../components/ui/button";
import { SidePanel } from "../../components/side-panel";
import { StatusBadge } from "../../components/status-badge";
import type { Task } from "../../lib/types";
import { insightFromTask } from "../../lib/task-insights";
import { formatDate, shortText } from "../../lib/utils";
import { TaskLogSheet } from "./task-log-sheet";

type MaintenanceAction = {
  task: Task;
};

const UNFINISHED_STATUSES = new Set(["queued", "leased", "running"]);

function isUnfinishedTask(task: Task | null | undefined) {
  return Boolean(task && UNFINISHED_STATUSES.has(task.status));
}

function leaseHasExpired(task: Task) {
  if (!task.locked_until) return true;
  const expiresAt = Date.parse(task.locked_until);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

function canForceFail(task: Task) {
  return task.status === "queued" || task.status === "running" || (task.status === "leased" && leaseHasExpired(task));
}

export function TasksPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [selected, setSelected] = useState<Task | null>(null);
  const [maintenanceAction, setMaintenanceAction] = useState<MaintenanceAction | null>(null);
  useEffect(() => {
    setPage(1);
  }, [perPage, status]);
  const tasks = useQuery({
    queryKey: ["tasks", status, page, perPage],
    queryFn: () => listTasks({ page, per_page: perPage, status }),
    refetchInterval: (query) => ((query.state.data?.tasks || []).some(isUnfinishedTask) ? 3000 : false)
  });
  const visibleTasks = tasks.data?.tasks || [];
  const hasUnfinishedVisibleTasks = visibleTasks.some(isUnfinishedTask);
  const summary = useQuery({ queryKey: ["task-summary"], queryFn: getTaskSummary, refetchInterval: hasUnfinishedVisibleTasks ? 3000 : false });
  const selectedTaskDetail = useQuery({
    queryKey: ["task", selected?.id],
    queryFn: () => getTask(selected!.id),
    enabled: Boolean(selected),
    refetchInterval: (query) => (isUnfinishedTask(query.state.data ?? selected) ? 2000 : false)
  });
  const selectedTask = selectedTaskDetail.data ?? selected;
  const logs = useQuery({
    queryKey: ["task-logs", selected?.id],
    queryFn: () => listTaskLogs(selected!.id),
    enabled: Boolean(selected),
    refetchInterval: isUnfinishedTask(selectedTask) ? 2000 : false
  });
  const summaryData = summary.data?.summary;
  const activeCount = summaryData?.active ?? visibleTasks.filter((task) => task.status === "queued" || task.status === "leased" || task.status === "running").length;
  const failedCount = summaryData?.failed ?? visibleTasks.filter((task) => task.status === "failed").length;
  const queuedCount = summaryData?.queued ?? visibleTasks.filter((task) => task.status === "queued").length;
  const runningCount = summaryData?.running ?? visibleTasks.filter((task) => task.status === "running").length;
  const retry = useMutation({
    mutationFn: retryTask,
    onSuccess: () => {
      toast.success("重试任务已创建");
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "重试失败")
  });
  const fail = useMutation({
    mutationFn: markTaskFailed,
    onSuccess: ({ message }) => {
      toast.success(message);
      setMaintenanceAction(null);
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      void queryClient.invalidateQueries({ queryKey: ["task-summary"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "标记失败失败")
  });

  function confirmMaintenanceAction() {
    if (!maintenanceAction) return;
    fail.mutate(maintenanceAction.task.id);
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <select className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部状态</option>
            <option value="active">queued/running</option>
            <option value="queued">queued</option>
            <option value="running">running</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
          </select>
          <Button onClick={() => setStatus("active")}>
            <Clock3 size={14} />
            活跃 {activeCount}
          </Button>
          <Button onClick={() => setStatus("failed")}>
            <AlertTriangle size={14} />
            失败 {failedCount}
          </Button>
        </div>
        <Button
          onClick={() => {
            void tasks.refetch();
            void summary.refetch();
          }}
        >
          <RefreshCw size={14} />
          刷新
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-5">
        <div className="rounded-md border bg-white p-2">总任务 <b>{summaryData?.total ?? tasks.data?.total ?? 0}</b></div>
        <div className="rounded-md border bg-white p-2">排队 <b>{queuedCount}</b></div>
        <div className="rounded-md border bg-white p-2">运行 <b>{runningCount}</b></div>
        <div className="rounded-md border bg-white p-2">失败 <b>{failedCount}</b></div>
        <div className="rounded-md border bg-white p-2">最近更新 <b>{formatDate(summaryData?.last_task_updated_at)}</b></div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-md border bg-white">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="border-b p-2">任务</th>
              <th className="border-b p-2">状态</th>
              <th className="border-b p-2">账号</th>
              <th className="border-b p-2">消息</th>
              <th className="border-b p-2">更新</th>
              <th className="border-b p-2 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleTasks.map((task) => {
              const insight = insightFromTask(task);
              return (
                <tr key={task.id} className="hover:bg-slate-50">
                  <td className="border-b p-2">
                    <div className="font-medium">{task.task_type}</div>
                    <div className="text-slate-500">{shortText(task.id, 8)}</div>
                  </td>
                  <td className="border-b p-2"><StatusBadge status={task.status} /></td>
                  <td className="border-b p-2">{task.account_id || task.discord_account_id || "-"}</td>
                  <td className="max-w-xl border-b p-2">
                    <div className="line-clamp-2 break-words">{task.latest_log_message || task.error_message || task.execute_message || "-"}</div>
                    {insight ? (
                      <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px]">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">阶段：{insight.stage}</span>
                        {insight.mailbox ? <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">邮箱：{insight.mailbox}</span> : null}
                        {insight.node ? <span className="rounded bg-violet-50 px-1.5 py-0.5 text-violet-700">节点：{insight.node}</span> : null}
                        {insight.code ? <span className="rounded bg-rose-50 px-1.5 py-0.5 text-rose-700">错误码：{insight.code}</span> : null}
                        {insight.hint ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">{insight.hint}</span> : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="border-b p-2">{formatDate(task.updated_at)}</td>
                  <td className="border-b p-2">
                    <div className="flex justify-end gap-1">
                      <Button onClick={() => setSelected(task)}>日志</Button>
                      {task.status === "failed" ? <Button onClick={() => retry.mutate(task.id)}>重试</Button> : null}
                      {canForceFail(task) ? (
                        <Button onClick={() => setMaintenanceAction({ task })}>标失败</Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!visibleTasks.length ? (
              <tr>
                <td colSpan={6} className="p-6 text-center text-xs text-slate-500">
                  {tasks.isLoading ? "任务加载中..." : "暂无任务"}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <PaginationBar total={tasks.data?.total ?? 0} page={page} perPage={perPage} onPageChange={setPage} onPerPageChange={setPerPage} />
      <SidePanel title="任务日志" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <TaskLogSheet task={selectedTask} logs={logs.data?.logs || []} />
      </SidePanel>
      {maintenanceAction ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b p-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <AlertTriangle size={16} />
                维护操作确认
              </div>
              <p className="mt-2 text-xs leading-5 text-slate-600">
                {maintenanceAction.task.status === "running"
                  ? "这是一个运行中的任务。标记失败只会更新任务记录，不会停止已经启动的 execution、浏览器 profile 或 worker；确认 worker 已停止后再继续。"
                  : maintenanceAction.task.status === "leased"
                  ? "该任务的租约已过期。服务端还会确认领取它的 worker 已离线，才允许标记为 failed。"
                  : "这会把尚未被 worker 领取的任务标记为 failed，不会删除任务记录。"}
              </p>
            </div>
            <div className="space-y-2 p-4 text-xs">
              <div className="rounded-md border bg-slate-50 p-2">任务：{maintenanceAction.task.task_type}</div>
              <div className="rounded-md border bg-slate-50 p-2">ID：{maintenanceAction.task.id}</div>
              <div className="rounded-md border bg-slate-50 p-2">当前状态：{maintenanceAction.task.status}</div>
            </div>
            <div className="flex justify-end gap-2 border-t p-3">
              <Button onClick={() => setMaintenanceAction(null)}>取消</Button>
              <Button
                disabled={fail.isPending}
                onClick={confirmMaintenanceAction}
                className="border-rose-200 bg-rose-600 text-white hover:bg-rose-700"
              >
                确认标失败
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
